import { motion } from "framer-motion";
import {
  BadgeCheck,
  ReceiptText,
  Factory,
  Globe2,
  ShoppingBag,
  Flag,
} from "lucide-react";

const CREDENTIALS = [
  {
    icon: BadgeCheck,
    title: "ISO Certified",
    code: "ISO 9001:2015",
    body: "Certified quality management system covering manufacturing, inspection and dispatch.",
  },
  {
    icon: ReceiptText,
    title: "GST Registered",
    code: "GSTIN Compliant",
    body: "Proper GST invoicing and e-way bills issued with every consignment.",
  },
  {
    icon: Factory,
    title: "MSME Registered",
    code: "Udyam Registered",
    body: "Registered micro, small & medium enterprise under the Udyam scheme.",
  },
  {
    icon: Globe2,
    title: "IEC Certified",
    code: "Import Export Code",
    body: "Import Export Code holder — cleared to accept and execute export orders.",
  },
  {
    icon: ShoppingBag,
    title: "GeM Registered",
    code: "Government e-Marketplace",
    body: "Registered seller on GeM for public sector and government procurement.",
  },
  {
    icon: Flag,
    title: "Make In India",
    code: "Manufactured in Gujarat",
    body: "Every fastener machined and finished at our own works in Vadodara, India.",
  },
];

export default function Credentials() {
  return (
    <section
      id="credentials"
      className="bg-navy-deep text-white py-20 lg:py-24"
      data-testid="credentials-section"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10 lg:mb-14"
        >
          <div>
            <div className="flex items-center gap-3 text-[11px] sm:text-xs tracking-[0.12em] uppercase text-blue-300 font-semibold mb-4">
              <span className="w-10 h-[2px] bg-brand" />
              Registrations & Compliance
            </div>
            <h2 className="font-display font-extrabold uppercase tracking-tight leading-[1.02] text-4xl sm:text-5xl">
              Registered,
              <br />
              certified, audit-ready
            </h2>
          </div>
          <p className="max-w-sm text-sm text-slate-400 leading-relaxed">
            Everything your purchase and vendor-registration team needs is
            already in place — documents can be shared on request for vendor
            enlistment or tender submission.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-px bg-slate-800 border border-slate-800">
          {CREDENTIALS.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                className="group relative bg-navy-deep px-5 sm:px-7 py-7 sm:py-9 overflow-hidden transition-colors duration-500"
                data-testid={`credential-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <span className="absolute inset-0 bg-gradient-to-br from-brand/0 to-brand/15 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <span className="relative inline-flex items-center justify-center w-16 h-16 mb-5">
                  <span className="absolute inset-0 rounded-full border-2 border-brand/70 group-hover:border-brand transition-colors duration-500" />
                  <span className="absolute inset-[5px] rounded-full border border-white/25" />
                  <span className="absolute inset-[5px] rounded-full bg-white/[0.06] group-hover:bg-brand transition-colors duration-500" />
                  <Icon size={22} className="relative text-blue-100 group-hover:text-white transition-colors duration-500" />
                </span>
                <h3 className="relative font-display font-bold uppercase text-base sm:text-lg leading-snug">
                  {c.title}
                </h3>
                <div className="relative font-sans font-semibold text-[11px] sm:text-[11px] tracking-[0.12em] uppercase text-blue-300 mt-2">
                  {c.code}
                </div>
                <p className="relative text-xs sm:text-[13px] text-slate-400 leading-relaxed mt-3 max-w-[22rem]">
                  {c.body}
                </p>
                <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-brand group-hover:w-full transition-all duration-700" />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
